
import { CellEditorSelectorResult, CellStyle, ColDef, ColGroupDef, ICellEditorParams, ICellRendererParams, ITooltipParams, RowClassParams } from "ag-grid-community";
import { addDays, format } from "date-fns";
import Enumerable from "linq";
import { round } from "lodash";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { LiquidProductionTATEnum } from "../../common/enums/LiquidProductionTATEnum";
import { ObjectFeaturesEnum } from "../../common/enums/ObjectFeaturesEnum";
import { SetPointEventTypeEnum } from "../../common/enums/SetPointEventTypeEnum";
import { TankEventEnum } from "../../common/enums/TankEventEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { ITagModel } from "../../common/models/ITagModel";
import { IKeyValuePair } from "../../common/models/KeyValuePair";
import { ITankStyle } from "../../common/models/TankStyle";
import { IFeetAndInchesCellEditorProps } from "../../components/ag-grid-extensions/cell-editors/FeetAndInchesCellEditor";
import { INumericCellEditorProps } from "../../components/ag-grid-extensions/cell-editors/NumericCellEditor";
import { RowDef } from "../../components/ag-grid-extensions/models/RowDef";
import { FooterCellRenderer } from "../../components/ag-grid-extensions/renderers/FooterCellRenderer";
import { IGaugeSheetColumnSetData, IGaugeSheetConfiguration } from "../../services/gauge-sheet-data-service/Models";
import i18n from "../../services/localization-service/i18n";
import { GaugeSheetActionItem } from "./GaugeSheetActions";
import { CustomCellEditorProps } from "ag-grid-react";
import { getTotalFromFeetAndInchesText } from "../../common/utilities/FeetAndInches";
import _ from "lodash";

export interface IMetaData {
    tagID: number,
    rawValue: number,
    color: string | undefined,
    backgroundColor: string | undefined,
    columnIndex: number;
    setPointEventType: SetPointEventTypeEnum | undefined,
    invalid?: boolean;
}

export interface IContractHour {
    hour: number;
    minutes: number;
}

const MANUAL_ENTRY_BACKGROUND_COLOR = 'rgb(211, 211, 211, .2)';
const CALCULATED_BACKGROUND_COLOR = 'rgb(41, 57, 85, .2)';
const TANKEVENT_BACKGROUND_COLOR = 'darkred';



function _getContractHour(contractHourText: string): IContractHour {
    let hour: number = 0;
    let minutes: number = 0;

    try {
        const arr = contractHourText.split(":");
        hour = parseInt(arr[0]);
        minutes = parseInt(arr[1]);
    }
    catch (err) {
        hour = 0;
        minutes = 0;
    }

    if (isNaN(hour)) {
        hour = 0;
    }

    if (isNaN(minutes)) {
        minutes = 0;
    }

    return {
        hour: hour,
        minutes: minutes
    };
}


const GaugeSheetUtils = {

    getActions(sourceActions: GaugeSheetActionItem[]) {

        let actions = [...sourceActions];

        return actions;
    },

    getRowClass (params: RowClassParams) : string | string[] | undefined {

        if ((params?.node?.rowIndex ?? 0) === 0) {
            return 'elynx-gauge-sheet-row-first';
        }

        if (params?.data?.tankEvent && params?.data?.tankEvent !== TankEventEnum.None) {
            return 'eLynx-gauge-sheet-row-tank-event';
            //$(eventData.row).attr("rv-tooltip", eventData.data.tankEventInfo);
        }
    },


    createGridRows: function (data: IGaugeSheetColumnSetData,
        configuration: IGaugeSheetConfiguration,
        tankLevelInches: boolean): RowDef[] {

        let totalRow: RowDef = {
            isTotalsRow: true

        };

        const totals: IKeyValuePair<string, number[]>[] = []

        const rows = data.rows.map((o, ndx) => {

        
            let row: RowDef = {
                ndx: ndx
            };
            
            const rowMetaData: IMetaData[] = o.MetaData;
            const tags: ITagModel[] = [];

            data.columns.forEach(v => {
                if (v.tatID && ((v.features & ObjectFeaturesEnum.FeetAndInches) === 0 || tankLevelInches) && !v.listID) {

                    let value = o[v.columnName] === '' ? '' : +((o[v.columnName] as string).replace(',', ''));

                    if (v.tatID === LiquidProductionTATEnum.TopLevel ||
                        v.tatID === LiquidProductionTATEnum.OilHeight ||
                        v.tatID === LiquidProductionTATEnum.WaterHeight ||
                        v.tatID === LiquidProductionTATEnum.InterfaceLevel)  {

                            if (o[v.columnName] === '' || o[v.columnName] === undefined) {
                                value = '';

                            } else {

                                const total = getTotalFromFeetAndInchesText(o[v.columnName])
                                if (total !== undefined) {
                                    value = _.round( total , 2);
                                } else {
                                    value = '';
                                }
                            }                                
                    }

                    row[v.columnName] = value;

                    if (v.aggregate && value !== '') {

                        let item = totals.find(o => o.Key === v.columnName);
                        if (!item) {
                            item = {
                                Key: v.columnName,
                                Value: []
                            };
                            totals.push(item);
                        }

                        item.Value.push(+value);
                    }
                } else {

                    if (v.columnName === 'day') {

                        if (o['Day'] === 'OS') {
                            row[v.columnName] = o['Day'];
                        } else {

                            const day = +o['Day'];

                            // the beginning date for the gaugesheet is always the last day of the previous month
                            const currentDate = addDays(new Date(new Date(data.date).toLocaleDateString()), 1);
                            currentDate.setDate(day);

                            const now = new Date();

                            if (currentDate.getTime() <= now.getTime()) {
                                row[v.columnName] = o['Day'];
                            } 
                        }

                    } else {
                        row[v.columnName] = o[v.columnName];
                    }
                }

                const tag: ITagModel = {
                    id: v.tagID,
                    objectType: eLynxObjectTypeEnum.Tag,
                    displayText: v.headerText,
                    tatID: v.tatID,
                    dataType: v.dataType,
                    features: v.features,
                    decimalPlaces: v.decimalPlaces,
                    lookupList: configuration.tagLookupList.find(x => x.id === v.listID),
                    uomID: v.uomID,
                    writeUpperBound: v.upperBound,
                    writeUpperBoundOp: v.upperBoundOp,
                    writeLowerBound: v.lowerBound,
                    writeLowerBoundOp: v.lowerBoundOp,
                    originalTagValue: {
                        displayText:  o[v.columnName === 'day' ? 'Day' : v.columnName]
                    }
                };

                tags.push(tag);
            });

            row['tags'] = tags;
            row['tankStyles'] = configuration.tankStyles;
            row['metaData'] = rowMetaData;
            row['tankEvent'] = o.TankEvent;
            row['tankEventInfo'] = o.TankEventInfo;
            row['allocationRatio'] = o.AllocationRatio;
            row['memoID'] = o.memoID;
            row['date'] = new Date(data.date).toLocaleDateString();

            return row;
        });

        data.columns.forEach(o => {

            let value: number | undefined;
            if (o.aggregate === 'sum') {
                const total = totals.find(v => v.Key === o.columnName);
                if (total?.Value.length) {
                    let sum = Enumerable.from(total?.Value).sum();
                    value = round(sum, 2);
                }
            } else if (o.aggregate === 'average') {
                const total = totals.find(v => v.Key === o.columnName);
                if (total?.Value.length) {
                    let avg = Enumerable.from(total?.Value).average();
                    value = round(avg, 2);
                }
            }

            totalRow[o.columnName] = value !== undefined ? value : '';
        });

        rows.push(totalRow);

        return rows;
    },
    createGridColumns: function (date: Date | undefined, 
        data: IGaugeSheetColumnSetData,
        showDevice: boolean,
        showTanks: boolean,
        onAddRunTicket: (day: number, tankID: number) => void,
        fontSize: number,
        spacing: number,
        isDesktop: boolean,
        tankLevelInches: boolean
    ): (ColDef | ColGroupDef)[] {

        const columns: (ColDef | ColGroupDef)[] = [];

        data.columns.forEach(o => {

            //if (o.objectType === eLynxObjectTypeEnum.None ||
            //    (o.objectType === eLynxObjectTypeEnum.Device && showDevice) ||
            //    (o.objectType === eLynxObjectTypeEnum.Tank && showTanks)) {

                const isFeetAndInches = !tankLevelInches && (o.features & ObjectFeaturesEnum.FeetAndInches) > 0;


                (o.tatID === LiquidProductionTATEnum.TopLevel || o.tatID === LiquidProductionTATEnum.InterfaceLevel) && tankLevelInches

                    const column: ColDef = {
                        headerName: (o.tatID === LiquidProductionTATEnum.TopLevel || o.tatID === LiquidProductionTATEnum.InterfaceLevel) && tankLevelInches ? `${o.headerText} (Inches)` : o.headerText,
                        field: o.columnName,
                        colId: `${o.columnName}-${data.deviceID}`,
                        //width: 140,
                        hide: (o.objectType === eLynxObjectTypeEnum.Device && !showDevice) || (o.objectType === eLynxObjectTypeEnum.Tank && !showTanks),
                        minWidth: !showTanks && o.columnName === 'remarks' ? 400 : isFeetAndInches ? 160 : 140,
                        //flex: o.columnName === 'remarks' ? 2 : 1, // => Bug 51745: Tag Values Grid Autosize Columns

                        //User Story 53907: Manual Entry Future Date Exclusion                        
                        // editable: (o.features & ObjectFeaturesEnum.ManualEntry) > 0 ? 
                        //             (params) => { 
                        //                 return params.data["day"] !== 'OS'
                        //             } : o.columnName === 'remarks' 
                        //                 && (o.features & ObjectFeaturesEnum.MemoModify) > 0  ? true : false,
                        editable: (params) => {

                            if (params.data["day"] === undefined || params.data["day"] === 'OS' || params.data.isTotalsRow) {
                                return false;
                            }
                            
                            if (date) {

                                const day = +(params.data["day"]);
                                const currentDate = new Date(date.toLocaleDateString());
                                currentDate.setDate(day);

                                const now = new Date();

                                if (currentDate.getTime() > now.getTime()) {
                                    return false;
                                }
                            }

                            if ((o.features & ObjectFeaturesEnum.ManualEntry) > 0) {
                                return params.data["day"] !== 'OS'                                
                            }

                            return o.columnName === 'remarks' && 
                                         (o.features & ObjectFeaturesEnum.MemoModify) > 0;

                        },
                        cellEditor: o.columnName === 'remarks' ? 'agLargeTextCellEditor' : undefined,
                        cellStyle: params => {

                            if (params.data["day"] !== 'OS') {


                                const tags: ITagModel[] = params.data?.tags ?? [];
                                const tag = tags.find(v => v.id === o.tagID);
                                
                                const metaData = params.data.metaData as IMetaData[];
                                const tagMetaData = metaData?.find(v => v.tagID === o.tagID);

                                const cellStyle: CellStyle = {
                                    borderColor: tagMetaData?.invalid ? 
                                                    'darkred' : 
                                                    '',
                                    backgroundColor: tagMetaData?.setPointEventType === SetPointEventTypeEnum.Manual ?
                                                        MANUAL_ENTRY_BACKGROUND_COLOR : 
                                                        tagMetaData?.setPointEventType === SetPointEventTypeEnum.Calculated && 
                                                            (tag?.tatID === LiquidProductionTATEnum.OilHaul || 
                                                                tag?.tatID === LiquidProductionTATEnum.WaterHaul ||
                                                                tag?.tatID === LiquidProductionTATEnum.TopLevel ||
                                                                tag?.tatID === LiquidProductionTATEnum.InterfaceLevel) ? 
                                                        CALCULATED_BACKGROUND_COLOR : 
                                                        ''
                                };
                                return cellStyle;
                            }
                            return null;
                        },
                        cellEditorPopup: o.columnName === 'remarks' || (!tankLevelInches && (o.features & ObjectFeaturesEnum.FeetAndInches) > 0),
                        cellEditorSelector: (params: ICellRendererParams | ICellEditorParams): CellEditorSelectorResult | undefined => {

                            // row['tags'] = tags;
                            // row['tankStyles'] = configuration.tankStyles;
                            // row['metaData'] = rowMetaData;

                            const tags: ITagModel[] = params.data?.tags ?? [];
                            const tag = tags.find(v => v.id === o.tagID);

                            const metaData = params.data.metaData as IMetaData[];
                            const tagMetaData = metaData?.find(v => v.tagID === o.tagID);

                            if (tag?.tatID) {
                                if (tag?.features && !tankLevelInches && (tag.features & ObjectFeaturesEnum.FeetAndInches) > 0) {
                                    const tankStyles: ITankStyle[] = params.data?.tankStyles ?? [];
                                    const tankStyle = tankStyles.find(v => v.id === o.tankStyleID);

                                    const currentDate = date ? new Date(date) : new Date();
                                    currentDate.setDate(params.data.day);
                                    const label = `${format(currentDate, 'LLL do')} - ${tag.displayText}`;
        
                                    return {
                                        component: isDesktop ? 'feetAndInchesSelectCellEditor' : 'feetAndInchesCellEditor',
                                        params: {
                                            maxFeet: (tankStyle?.heightInInches ?? 0) / 12,
                                            //label: tag.displayText
                                            label: label,
                                            showDelete: params.value !== '' && tagMetaData?.setPointEventType === SetPointEventTypeEnum.Manual
                                        } as IFeetAndInchesCellEditorProps
                                    };
                                } else if (tag?.lookupList) {
                                    return {
                                        component: 'agRichSelectCellEditor',
                                        popup: true,
                                        params: {
                                            values: ['', ...tag.lookupList?.listText.map((o) => o.description)]
                                        }
                                    };
                                } else {
                                    return {
                                        component: 'numericCellEditor'
                                    };
                                }
                            }
                        },
                        //cellEditorParams: (params: ICellEditorParams) => {
                        cellEditorParams: (params: CustomCellEditorProps) => {

                            if (params.colDef.field === 'remarks') {
                                return  {
                                    maxLength: 8000,
                                    rows: 4,
                                    cols: 40
                                }
                            }

                            const tags: ITagModel[] = params.data?.tags ?? [];
                            const tag = tags.find(v => v.id === o.tagID);

                            const metaData = params.data.metaData as IMetaData[];
                            const tagMetaData = metaData?.find(v => v.tagID === o.tagID);

                            const features = tag ? tag.features as ObjectFeaturesEnum : ObjectFeaturesEnum.None;
                            const isHaul = (tag?.tatID === LiquidProductionTATEnum.OilHaul || 
                                                tag?.tatID === LiquidProductionTATEnum.WaterHaul) && 
                                                    tagMetaData?.setPointEventType !== SetPointEventTypeEnum.Manual;
                            if (!tag?.listID && (((features & ObjectFeaturesEnum.FeetAndInches) === 0) || tankLevelInches)) {


                                const tankStyles: ITankStyle[] = params.data?.tankStyles ?? [];
                                const tankStyle = tankStyles.find(v => v.id === o.tankStyleID);

                                const cellEditorParams: INumericCellEditorProps = {
                                    ...params,
                                    decimals: tag?.decimalPlaces,
                                    lowerBound: tag?.writeLowerBound,
                                    lowerBoundOp: tag?.writeLowerBoundOp,
                                    upperBound: !tankLevelInches ? tag?.writeUpperBound : tankStyle?.heightInInches,
                                    upperBoundOp: !tankLevelInches ? tag?.writeUpperBoundOp : '<=',
                                    showCornerIcon: isHaul,
                                    cornerIconTitle: isHaul ? i18n.t('liquidProd.addRunTicket') as string : '',
                                    onCornerIconClicked: isHaul ? () => {
                                        const day = +params.data["day"];
                                        onAddRunTicket(day, o.objectID) 
                                    }: undefined

                                };
                                return cellEditorParams;
                            } else {
                                return params;
                            }
                        },
                        cellRendererParams: (params: ICellRendererParams) => {
                            if (params.node.rowPinned) {
                                return {...params,
                                    aggregate: o.aggregate
                                }
                            } else {
                                return params;
                            }
                        },
                        cellRendererSelector: (params: ICellRendererParams) => {
                            if (params.node.rowPinned) {
                                return {
                                    component: FooterCellRenderer
                                };
                            } else {
                            // rows that are not pinned don't use any cell renderer
                            return undefined;
                            }
                        },
                        tooltipValueGetter: (params: ITooltipParams) => {
                            return params.data?.tankEventInfo;
                        },
                        cellDataType: "text"
                    };
        
                    columns.push(column);
        
            //    }
        });

        const columnGroups: ColGroupDef[] = [{
            headerName: format(date ?? new Date(), 'LLL yyyy'),
            children: [{
                headerName: 'Day',
                field: 'day',
                sortable: false,
                width: (5 * fontSize) + (2 * spacing),
                pinned: true,
                resizable: false
    
            }]
        }];
        let notShownCount = 0;
        data.columnSets.forEach(o => {

            //if (o.objectType === eLynxObjectTypeEnum.None ||
            //    (o.objectType === eLynxObjectTypeEnum.Device && showDevice) ||
            //    (o.objectType === eLynxObjectTypeEnum.Tank && showTanks)) {
                    
                const columnGroup: ColGroupDef = {
                    headerGroupComponent: o.objectType === eLynxObjectTypeEnum.Tank ? 'customHeaderGroup' : undefined,
                    headerGroupComponentParams: o.objectType === eLynxObjectTypeEnum.Tank ? {
                                                id: o.objectID,
                                                objectType: o.objectType,
                                                displayText: o.title.text
                                            } as IeLynxObject : undefined,
                    headerName: o.title.text,
                    headerTooltip: o.title.tooltip,
                    
                    children: Enumerable.from(columns).skip(o.startIndex - notShownCount).take(o.title.count).toArray(),
                };

                columnGroups.push(columnGroup);
            //} else {
            //    notShownCount += o.title.count;
            //}
        });

        return columnGroups;
        // return [{
        //     headerName: 'Day',
        //     field: 'day',
        //     sortable: false,
        //     width: 50,
        //     pinned: true
        // }, ...columnGroups];
    },
    isLiquidProductionTAT: function(tatID: number, config: IGaugeSheetConfiguration): boolean {
        return (tatID === LiquidProductionTATEnum.TopLevel ||
            tatID === LiquidProductionTATEnum.InterfaceLevel ||
            tatID === LiquidProductionTATEnum.OilHaul ||
            tatID === LiquidProductionTATEnum.WaterHaul ||
            tatID === LiquidProductionTATEnum.DailyOilProduction ||
            tatID === LiquidProductionTATEnum.DailyWaterProduction ||
            (config.oilDisposalTatIDs && Enumerable.from(config.oilDisposalTatIDs).any(o => o === tatID)) ||
            (config.waterDisposalTatIDs && Enumerable.from(config.waterDisposalTatIDs).any(o => o === tatID)) ||
            (config.oilAllocationTatIDs && Enumerable.from(config.oilAllocationTatIDs).any(o => o === tatID)) ||
            (config.waterAllocationTatIDs && Enumerable.from(config.waterAllocationTatIDs).any(o => o === tatID)));
    },
    getTagContractHour: function (contractHour: string): IContractHour {
        if (contractHour === undefined) {
            contractHour = "00:00";
        }

        return _getContractHour(contractHour);
    },
    parseDateFromString: function (value: string): Date { 
        // values are stored as UTC with no offset
        //"2022-05-02T00:00:00+00:00"
        
        const timestampString = value.split('T');
        let [dateString] = timestampString;
        let dateParts = dateString.split('-');
        let [year, month, day] = dateParts;

        return new Date(+year, (+month) - 1, +day);
    }
    
};

export default GaugeSheetUtils;

export { MANUAL_ENTRY_BACKGROUND_COLOR, CALCULATED_BACKGROUND_COLOR, TANKEVENT_BACKGROUND_COLOR }

